import envConfig from "../../../envConfig";

/**
 * Parse SSE data line and return the parsed JSON object
 * @param {string} line - The SSE data line (e.g., "data: {...}")
 * @returns {object|null} - Parsed JSON object or null if parsing fails
 */
const parseSSELine = (line) => {
  if (!line || !line.startsWith("data: ")) return null;
  try {
    const jsonStr = line.slice(6); // Remove "data: " prefix
    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn("Failed to parse SSE line:", line, e);
    return null;
  }
};

// Inactivity timeout: 5 minutes (300,000 ms)
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

// Generic error message for public-facing widget
const PUBLIC_ERROR_MESSAGE =
  "An error occurred processing your request please try again later";

/**
 * Stream agent response using Server-Sent Events (SSE)
 * @param {object} params - The streaming parameters
 * @param {string} params.chatid - Chat ID (optional, will be created if not provided)
 * @param {string} params.workspaceid - Workspace ID
 * @param {string} params.token - API token
 * @param {Array} params.messages - Array of message objects with text and role
 * @param {string} params.agentid - Agent ID
 * @param {string} [params.baseImage] - Base image for vision models
 * @param {string} [params.canvasSelection] - Canvas selection data
 * @param {AbortSignal} params.signal - AbortController signal for cancellation
 * @param {function} params.onToken - Callback for text tokens: (chunk: string, messageId: string) => void
 * @param {function} params.onMessage - Callback for status/metadata updates: (data: object) => void
 * @param {function} params.onError - Callback for errors: (error: Error) => void
 * @param {function} params.onComplete - Callback for stream completion: () => void
 */
const streamAgentResponse = async ({
  chatid,
  workspaceid,
  token,
  messages,
  agentid,
  baseImage,
  canvasSelection,
  signal,
  onToken,
  onMessage,
  onError,
  onComplete,
}) => {
  const url = `${envConfig.REACT_APP_AIS_ENDPOINT}/agent`;

  // Inactivity timeout management
  let inactivityTimeoutId = null;
  let reader = null;

  const clearInactivityTimeout = () => {
    if (inactivityTimeoutId) {
      clearTimeout(inactivityTimeoutId);
      inactivityTimeoutId = null;
    }
  };

  const resetInactivityTimeout = () => {
    clearInactivityTimeout();
    inactivityTimeoutId = setTimeout(() => {
      console.warn("SSE stream timed out due to 5 minutes of inactivity");
      if (reader) {
        reader.cancel("Inactivity timeout");
      }
      onError?.(new Error("Stream timed out due to inactivity"));
    }, INACTIVITY_TIMEOUT_MS);
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
        "x-client-source": "toothfairyai",
      },
      body: JSON.stringify({
        workspaceid,
        chatid,
        messages,
        agentid,
        base_image: baseImage,
        canvas_selection: canvasSelection,
        raw_stream: true,
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentMessageId = null;

    // Start the inactivity timeout
    resetInactivityTimeout();

    while (true) {
      const { done, value } = await reader.read();

      // Reset timeout on any data received
      if (value) {
        resetInactivityTimeout();
      }

      if (done) {
        clearInactivityTimeout();
        // Process any remaining data in buffer
        if (buffer.trim()) {
          const lines = buffer.split("\n");
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              const parsed = parseSSELine(trimmedLine);
              if (parsed) {
                const streamError = processSSEEvent(
                  parsed,
                  currentMessageId,
                  onToken,
                  onMessage
                );
                if (streamError) {
                  onError?.(streamError);
                  return;
                }
                if (parsed.message_id) currentMessageId = parsed.message_id;
              }
            }
          }
        }
        onComplete?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const parsed = parseSSELine(trimmedLine);
        if (parsed) {
          const streamError = processSSEEvent(
            parsed,
            currentMessageId,
            onToken,
            onMessage
          );
          if (streamError) {
            clearInactivityTimeout();
            if (reader) {
              reader.cancel("Stream error");
            }
            onError?.(streamError);
            return;
          }
          if (parsed.message_id) currentMessageId = parsed.message_id;
        }
      }
    }
  } catch (error) {
    clearInactivityTimeout();
    if (error.name === "AbortError") {
      console.log("Stream aborted by user");
    } else {
      console.error("Stream error:", error);
      // Use generic message for public-facing widget
      onError?.(new Error(PUBLIC_ERROR_MESSAGE));
    }
  }
};

/**
 * Process a single SSE event and call appropriate callbacks
 * @returns {Error|null} - Returns an error if one is detected, null otherwise
 */
const processSSEEvent = (data, currentMessageId, onToken, onMessage) => {
  // Handle error events from the stream (any format)
  // Format 1: { error: { ... } }
  // Format 2: { type: 'error', errorCode, errorMessage, ... }
  if (data.error || data.type === "error") {
    return new Error(PUBLIC_ERROR_MESSAGE);
  }

  // Handle connection status
  if (data.status === "connected") {
    onMessage?.({ type: "connection", status: "connected" });
    return null;
  }

  // Handle chat creation
  if (data.chat_created) {
    onMessage?.({
      type: "chat_created",
      chatid: data.chatid,
      message_id: data.message_id,
    });
    return null;
  }

  // Handle token events (text streaming)
  if (data.type === "token" && data.chunk) {
    onToken?.(data.chunk, data.message_id || currentMessageId);
    return null;
  }

  // Handle message events (status/metadata updates)
  if (data.type === "message") {
    onMessage?.({
      type: "message",
      message_id: data.message_id,
      status: data.status,
      metadata: data.metadata,
      callbackMetadata: data.callbackMetadata,
      text: data.text,
      chatid: data.chatid,
      files: data.files,
      images: data.images,
      audios: data.audios,
      videos: data.videos,
    });
    return null;
  }

  // Forward any other events
  onMessage?.(data);
  return null;
};

export default {
  streamAgentResponse,
};
